import { Check, Copy } from 'lucide-react'
import { useMemo, useState } from 'react'

import { CodeView, type CodeViewLang } from '@/components/code/code-view'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useClipboard } from '@/hooks/use-clipboard'
import { type Lang, LANGS, type SnippetInput, snippets } from '@/lib/snippets'

const CODE_LANG: Record<Lang, CodeViewLang> = {
  cli: 'bash',
  typescript: 'typescript',
  curl: 'bash',
}

export function CodePanel(input: SnippetInput) {
  const [lang, setLang] = useState<Lang>('cli')
  const { copy, copied } = useClipboard()
  const all = useMemo(() => snippets(input), [input])
  const code = all[lang]
  return (
    <section aria-label="Code" className="flex min-h-0 flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-heading text-sm font-semibold">API request</h2>
        <Tabs value={lang} onValueChange={(v) => setLang(v as Lang)}>
          <TabsList>
            {LANGS.map((l) => (
              <TabsTrigger key={l.id} value={l.id} className="text-xs">
                {l.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>
      <CodeView
        code={code}
        lang={CODE_LANG[lang]}
        theme="dark"
        className="max-h-[52vh]"
        actions={
          <Button variant="secondary" size="sm" onClick={() => copy(code)} aria-live="polite">
            {copied ? <Check data-icon="inline-start" /> : <Copy data-icon="inline-start" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
        }
      />
      <p className="text-[11px] text-muted-foreground">
        {input.templateUrl
          ? 'Uses the public sample template and exact field values — no source document or model call.'
          : input.sampleUrl
            ? 'Runs verbatim with your key — the sample URL is public.'
            : input.stop.kind === 'none'
              ? 'Runs verbatim with your key — edit the data and re-run.'
              : 'Two steps for your own file: register it, then run the verb on its id.'}
      </p>
    </section>
  )
}
